import type { Usage } from "@/app/queries";
import { plural } from "@/lib/plural";
import { AsideHead } from "./shared";

/** Шкала для медианы: две недели на шаг — полная полоса. */
const MEDIAN_SCALE_DAYS = 14;

/** «Как идёт у студентов»: на шаге сейчас, медиана времени, гейт сдан — из usage шага. */
export function UsageCard({ usage, status, error, retry }: { usage?: Usage; status: "pending" | "error" | "success"; error?: string; retry?: () => void }) {
  return (
    <section className="rounded-md border border-bd bg-card p-3.5" data-testid="usage">
      <AsideHead title="как идёт у студентов" />
      {status === "pending" && <div className="font-mono text-[11.5px] text-mut">считаем…</div>}
      {status === "error" && (
        <div className="flex flex-col gap-2 text-[12px] leading-[1.5] text-ink2">
          <span>Не удалось посчитать: {error ?? "нет связи"}. На документ шага это не влияет.</span>
          {retry && (
            <button type="button" onClick={retry} className="w-fit cursor-pointer rounded-sm border border-bd2 bg-transparent px-2.5 py-1 font-mono text-[11px] text-ink2">
              повторить
            </button>
          )}
        </div>
      )}
      {status === "success" && usage && <Bars u={usage} />}
    </section>
  );
}

function Bars({ u }: { u: Usage }) {
  const cohort = u.cohort_size;
  const median = u.median_days;
  const slow = median !== undefined && median > 7;
  const rows = [
    { k: "на шаге сейчас", v: `${u.on_step} из ${cohort}`, w: cohort ? u.on_step / cohort : 0, fg: "var(--acc)" },
    { k: "медиана времени", v: median === undefined ? "—" : `${median} ${plural(median, "день", "дня", "дней")}`, w: median === undefined ? 0 : Math.min(1, median / MEDIAN_SCALE_DAYS), fg: slow ? "var(--warn)" : "var(--ink2)" },
    { k: "гейт сдан", v: `${u.gate_passed} ${plural(u.gate_passed, "студент", "студента", "студентов")}`, w: cohort ? u.gate_passed / cohort : 0, fg: u.gate_passed ? "var(--acc)" : "var(--mut)" },
  ];
  return (
    <>
      <div className="flex flex-col gap-2.5">
        {rows.map((r) => (
          <div key={r.k}>
            <div className="mb-[5px] flex justify-between gap-2.5 font-mono text-[11.5px]">
              <span className="text-mut">{r.k}</span>
              <span style={{ color: r.fg }}>{r.v}</span>
            </div>
            <div className="h-1 overflow-hidden rounded-[2px] bg-card2" role="img" aria-label={`${r.k}: ${r.v}`}>
              <div className="h-full" style={{ width: `${Math.round(r.w * 100)}%`, background: r.fg }} />
            </div>
          </div>
        ))}
      </div>
      {slow && <p className="m-0 mt-3 text-[12px] leading-[1.5] text-mut text-pretty">На шаге сидят дольше медианы. Возможно, практика «применение» слишком тяжёлая для одного захода.</p>}
      {cohort === 0 && <p className="m-0 mt-3 text-[12px] leading-[1.5] text-mut text-pretty">На шаге ещё никого не было — цифры появятся, когда студенты дойдут до него.</p>}
    </>
  );
}
